import { useQuery } from '@tanstack/react-query'
import { useNavigate, useSearch } from '@tanstack/react-router'

import type { EntityType } from '@/bindings/EntityType'
import { Button } from '@/components/button'
import { Field, Input, Select } from '@/components/field'
import { FormError } from '@/components/form-error'
import { Card, Page } from '@/components/layout'
import { Pager } from '@/components/pager'
import { type AuditFilter, isEntityType, RESTAURANT_ENTITY_TYPES, restaurantAuditLogQuery } from '@/features/audit-log/api'
import { AuditEntryRow } from '@/features/audit-log/entry-row'
import { useManagedRestaurant } from '@/features/back-office/scope'
import { useT } from '@/lib/i18n/translate'
import { useClampedPage } from '@/lib/paging'
import { canManageRestaurant } from '@/lib/roles'

const ROUTE_ID = '/$restaurantSlug/manage/audit-log'
const PATH = '/$restaurantSlug/manage/audit-log'

/**
 * The restaurant's own record (I2): who changed a setting, the menu, an
 * account, an order or a session, and from what to what. Manager and above;
 * the filters live in the URL so a view can be shared.
 */
export function RestaurantAuditLogPage() {
  const t = useT()
  const { restaurantId, context } = useManagedRestaurant()
  return (
    <Page width="lg" title={t('audit.title')} className="flex flex-col gap-4">
      <p className="text-muted text-sm">{t('audit.intro')}</p>
      {canManageRestaurant(context.role) ? (
        <LogList restaurantId={restaurantId} branches={context.branches} />
      ) : (
        <p role="alert" className="text-muted text-sm">
          {t('audit.managerOnly')}
        </p>
      )}
    </Page>
  )
}

function LogList({ restaurantId, branches }: { restaurantId: string; branches: { id: string; name: string }[] }) {
  const t = useT()
  const navigate = useNavigate()
  const { page, ...filter } = useSearch({ from: ROUTE_ID })
  const list = useQuery(restaurantAuditLogQuery(restaurantId, filter, page))
  const { restaurantSlug } = useManagedRestaurant()
  const params = { restaurantSlug }
  const link = (page: number) => ({ to: PATH, params, search: { ...filter, page } }) as const
  useClampedPage(page, list.data, link)

  // Every filter change starts over at page 1; an empty value drops the key from the URL.
  function set(key: keyof AuditFilter, value: string) {
    const next: Record<string, string> = { ...filter, [key]: value }
    for (const name of Object.keys(next)) if (!next[name]) delete next[name]
    void navigate({ to: PATH, params, search: { ...(next as AuditFilter), page: 1 } })
  }

  return (
    <>
      <Card className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Field label={t('audit.filter.branch')}>
          <Select value={filter.branch ?? ''} onChange={(event) => set('branch', event.target.value)}>
            <option value="">{t('audit.filter.allBranches')}</option>
            {branches.map((branch) => (
              <option key={branch.id} value={branch.id}>
                {branch.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t('audit.filter.entity')}>
          <Select value={filter.entity ?? ''} onChange={(event) => set('entity', isEntityType(event.target.value) ? event.target.value : '')}>
            <option value="">{t('audit.filter.allEntities')}</option>
            {RESTAURANT_ENTITY_TYPES.map((entity: EntityType) => (
              <option key={entity} value={entity}>
                {t(`audit.entity.${entity}`)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t('audit.filter.from')}>
          <Input type="date" value={filter.from ?? ''} onChange={(event) => set('from', event.target.value)} />
        </Field>
        <Field label={t('audit.filter.to')}>
          <Input type="date" value={filter.to ?? ''} onChange={(event) => set('to', event.target.value)} />
        </Field>
      </Card>

      {list.isPending && <p className="text-muted text-sm">{t('common.loading')}</p>}
      {list.error && (
        <div className="flex flex-col gap-2">
          <FormError error={list.error} />
          <Button variant="secondary" onClick={() => void list.refetch()} className="self-start">
            {t('common.retry')}
          </Button>
        </div>
      )}
      {list.data &&
        (list.data.items.length === 0 ? (
          <Card>
            <p className="text-muted text-sm">{t('audit.empty')}</p>
          </Card>
        ) : (
          <ul className="flex flex-col gap-2">
            {list.data.items.map((entry) => (
              <li key={entry.id}>
                <AuditEntryRow entry={entry} />
              </li>
            ))}
          </ul>
        ))}
      <Pager label={t('audit.pager')} page={page} total={list.data?.total ?? 0} link={link} />
    </>
  )
}
